// Chakra imports
import { ChakraProvider, Portal, useDisclosure } from '@chakra-ui/react';
import Configurator from 'components/Configurator/Configurator';
import Footer from 'components/Footer/Footer.js';
// Layout components
import AdminNavbar from 'components/Navbars/AdminNavbar.js';
import Sidebar from 'components/Sidebar';
import React, { useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
// Custom Chakra theme
import theme from 'theme/theme.js';
import FixedPlugin from '../components/FixedPlugin/FixedPlugin';
// Custom components
import MainPanel from '../components/Layout/MainPanel';
import PanelContainer from '../components/Layout/PanelContainer';
import PanelContent from '../components/Layout/PanelContent';
import { isTokenValid, isPublicPath, canManageData, isAdmin } from 'utils/auth';
import { getAllDashRoutes, getSidebarRoutes } from 'routes';

export default function Dashboard(props) {
	const location = useLocation();
	const [ sidebarVariant, setSidebarVariant ] = useState('transparent');
	const [ fixed, setFixed ] = useState(false);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const authenticated = isTokenValid();
	const currentPath = location.pathname;

	// Unauthenticated visitor guard
	if (!authenticated) {
		if (currentPath === '/txd' || currentPath === '/txd/' || currentPath === '/txd/dashboard') {
			return <Redirect to="/txd/mapdata" />;
		}
		if (!isPublicPath(currentPath)) {
			return <Redirect to="/auth/signin" />;
		}
	}

	const sidebarRoutes = getSidebarRoutes();
	const allRoutes = getAllDashRoutes();

	const { ...rest } = props;

	const getRoute = () => {
		return window.location.pathname !== 'admin/full-screen-maps';
	};

	const getActiveRoute = (routes) => {
		let activeRoute = 'Default Brand Text';
		for (let i = 0; i < routes.length; i++) {
			if (routes[i].collapse) {
				let collapseActiveRoute = getActiveRoute(routes[i].views);
				if (collapseActiveRoute !== activeRoute) {
					return collapseActiveRoute;
				}
			} else if (routes[i].category) {
				let categoryActiveRoute = getActiveRoute(routes[i].views);
				if (categoryActiveRoute !== activeRoute) {
					return categoryActiveRoute;
				}
			} else {
				if (window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1) {
					return routes[i].name;
				}
			}
		}
		return activeRoute;
	};

	const getActiveNavbar = (routes) => {
		let activeNavbar = false;
		for (let i = 0; i < routes.length; i++) {
			if (routes[i].category) {
				let categoryActiveNavbar = getActiveNavbar(routes[i].views);
				if (categoryActiveNavbar !== activeNavbar) {
					return categoryActiveNavbar;
				}
			} else {
				if (window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1) {
					if (routes[i].secondaryNavbar) {
						return routes[i].secondaryNavbar;
					}
				}
			}
		}
		return activeNavbar;
	};

	// Guarded route renderer
	const renderGuardedComponent = (Component, routeMeta) => {
		return (routeProps) => {
			const isPublic = routeMeta.isPublic || isPublicPath(routeMeta.fullPath);

			// Public check
			if (!authenticated && !isPublic) {
				return <Redirect to="/auth/signin" />;
			}

			// Role permission check for admin-only routes
			if (routeMeta.requiresAdmin && !isAdmin()) {
				return <Redirect to="/txd/dashboard" />;
			}

			// Role permission check for management-only routes
			if (routeMeta.requiresManagement && !canManageData()) {
				if (routeMeta.fullPath.includes('/individuals')) {
					return <Redirect to="/txd/individuals" />;
				}
				if (routeMeta.fullPath.includes('/families')) {
					return <Redirect to="/txd/families" />;
				}
				return <Redirect to="/txd/dashboard" />;
			}

			return <Component {...routeProps} />;
		};
	};

	const getRoutes = (routes) => {
		return routes.flatMap((route, key) => {
			if (route.category && route.views) {
				return getRoutes(route.views);
			}

			if (route.children) {
				const parentFullPath = `${route.layout}${route.path}`;
				return [
					<Route
						path={parentFullPath}
						render={renderGuardedComponent(route.component, {
							...route,
							fullPath: parentFullPath,
						})}
						exact
						key={`${key}-parent`}
					/>,
					...route.children.map((child, childKey) => {
						const childFullPath = `${route.layout}${route.path}${child.path}`;
						return (
							<Route
								path={childFullPath}
								render={renderGuardedComponent(child.component, {
									...child,
									fullPath: childFullPath,
									requiresManagement: child.requiresManagement || route.requiresManagement,
								})}
								exact
								key={`${key}-${childKey}-child`}
							/>
						);
					}),
				];
			}

			if (route.layout === "/txd") {
				const fullPath = `${route.layout}${route.path}`;
				return (
					<Route
						path={fullPath}
						render={renderGuardedComponent(route.component, {
							...route,
							fullPath,
						})}
						exact
						key={key}
					/>
				);
			}
			return [];
		});
	};

	document.documentElement.dir = 'ltr';

	return (
		<ChakraProvider theme={theme} resetCss={false}>
			<Sidebar
				routes={sidebarRoutes}
				logoText={'TUNISIAN XP DATABASE'}
				display='none'
				sidebarVariant={sidebarVariant}
				{...rest}
			/>
			<MainPanel
				w={{
					base: '100%',
					xl: 'calc(100% - 275px)'
				}}>
				<Portal>
					<AdminNavbar
						onOpen={onOpen}
						logoText={'TUNISIAN XP DATABASE'}
						brandText={getActiveRoute(sidebarRoutes)}
						secondary={getActiveNavbar(sidebarRoutes)}
						fixed={fixed}
						{...rest}
					/>
				</Portal>
				{getRoute() ? (
					<PanelContent>
						<PanelContainer>
							<Switch>
								{getRoutes(allRoutes)}
								{authenticated ? (
									<Redirect from='/txd' to='/txd/dashboard' />
								) : (
									<Redirect from='/txd' to='/txd/mapdata' />
								)}
							</Switch>
						</PanelContainer>
					</PanelContent>
				) : null}
				<Footer />
				<Portal>
					<FixedPlugin secondary={getActiveNavbar(sidebarRoutes)} fixed={fixed} onOpen={onOpen} />
				</Portal>
				<Configurator
					secondary={getActiveNavbar(sidebarRoutes)}
					isOpen={isOpen}
					onClose={onClose}
					isChecked={fixed}
					onSwitch={(value) => {
						setFixed(value);
					}}
					onOpaque={() => setSidebarVariant('opaque')}
					onTransparent={() => setSidebarVariant('transparent')}
				/>
			</MainPanel>
		</ChakraProvider>
	);
}
